import { ApolloError } from "@apollo/client";
import { useCallback, useEffect, useMemo, useRef } from "react";
import _ from "lodash";
import { useAppContext } from "useAppContext";
import { PostHogExceptionDataProps, usePostHog } from "shared/hooks";
import {
  EnumUserAccessOperatorsStatus,
  EnumUserQuickLinksType,
  EnumUserUserType,
  useUserSaveQuickLinksMutation,
} from "gql/generated";
import { getMultilingualString } from "shared/utils";

export interface QuickLink {
  title: string;
  link: string;
  lastVisited: string;
  type: EnumUserQuickLinksType;
  entityId: string;
  pinned?: boolean;
}

const LEGACY_STORAGE_KEY = "waQuickLinks";

/** Normalizes GraphQL / Mongo id shapes so keys match server `quickLinkKey`. */
export const normalizeEntityId = (value: unknown): string => {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (
    typeof value === "object" &&
    value !== null &&
    "$oid" in value &&
    typeof (value as { $oid: string }).$oid === "string"
  ) {
    return (value as { $oid: string }).$oid;
  }
  return String(value);
};

const getOrgAppSegment = (link: string): string | null => {
  const path = link.split("?")[0]?.split("#")[0] ?? "";
  const match = path.match(/^\/app\/organization\/[^/]+\/([^/]+)/);
  return match?.[1] ? match[1].replace(/-/g, "_").toUpperCase() : null;
};

/**
 * Migrate legacy localStorage quick links to the backend on first load.
 * Returns the migrated links if any, and clears localStorage.
 */
const migrateLegacyQuickLinks = (
  email: string,
  trackPostHogException: (
    error: unknown,
    data: PostHogExceptionDataProps,
  ) => void,
): QuickLink[] => {
  try {
    const stored = localStorage.getItem(LEGACY_STORAGE_KEY + "_" + email);
    if (!stored) return [];
    const parsed: QuickLink[] = JSON.parse(stored);
    localStorage.removeItem(LEGACY_STORAGE_KEY + "_" + email);
    return parsed;
  } catch (error) {
    trackPostHogException(error, {
      fileName: "useQuickLinks.ts",
      method: "migrateLegacyQuickLinks",
      message: "Error migrating legacy quick links from localStorage",
    });
    return [];
  }
};

/**
 * Rebuild an array keeping pinned items at their stored indices
 * and filling remaining slots with unpinned items in order.
 */
const buildWithPinnedSlots = (
  persisted: QuickLink[],
  unpinnedPool: QuickLink[],
): QuickLink[] => {
  const pinnedSlots = new Map<number, QuickLink>();
  persisted.forEach((ql, i) => {
    if (ql.pinned) pinnedSlots.set(i, ql);
  });

  const result: QuickLink[] = [];
  let poolIdx = 0;
  const totalLen = Math.max(
    persisted.length,
    (pinnedSlots.size > 0
      ? Math.max(...pinnedSlots.keys()) + 1
      : 0) + unpinnedPool.length,
  );

  for (let i = 0; i < totalLen; i++) {
    if (pinnedSlots.has(i)) {
      result.push(pinnedSlots.get(i)!);
    } else if (poolIdx < unpinnedPool.length) {
      result.push(unpinnedPool[poolIdx++]);
    }
  }

  while (poolIdx < unpinnedPool.length) {
    result.push(unpinnedPool[poolIdx++]);
  }

  return result;
};

export const useQuickLinks = () => {
  const { dbUser, operators, organizations, refetchDbUser } = useAppContext();
  const { trackPostHogException } = usePostHog();
  const migrationAttempted = useRef(false);
  const pruneAttempted = useRef(false);
  /** Serializes quick-link saves so two effects cannot fire overlapping userUpdates. */
  const persistChainRef = useRef(Promise.resolve());
  /** Optimistic local state: holds the latest desired quick links while a debounce is pending. */
  const pendingLinksRef = useRef<QuickLink[] | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const [saveQuickLinks] = useUserSaveQuickLinksMutation();

  const persistedQuickLinks: QuickLink[] = useMemo(
    () =>
      (dbUser?.quickLinks ?? [])
        .filter((ql): ql is NonNullable<typeof ql> => ql != null)
        .map(ql => ({
          title: ql.title,
          link: ql.link,
          lastVisited: ql.lastVisited,
          type: ql.type as EnumUserQuickLinksType,
          entityId: ql.entityId,
          pinned: ql.pinned ?? false,
        })),
    [dbUser?.quickLinks],
  );

  const enrolledOrganizationEntityIdSet = useMemo(
    () =>
      new Set(
        (dbUser?.access?.organizations ?? [])
          .map(a => normalizeEntityId(a?.entityId))
          .filter((id): id is string => Boolean(id)),
      ),
    [dbUser?.access?.organizations],
  );

  /** Ensures every organization in access has a quick link before persisting or displaying. */
  const mergeRequiredOrganizationQuickLinks = useCallback(
    (links: QuickLink[]): QuickLink[] => {
      const existingOrgIds = new Set(
        links
          .filter(ql => ql.type === EnumUserQuickLinksType.Organization)
          .map(ql => normalizeEntityId(ql.entityId)),
      );
      const additions: QuickLink[] = [];
      for (const id of enrolledOrganizationEntityIdSet) {
        if (existingOrgIds.has(id)) continue;
        const org = organizations.find(
          o => normalizeEntityId(o._id) === id,
        );
        additions.push({
          title: org ? getMultilingualString(org.name) : id,
          link: `/app/organization/${id}`,
          lastVisited: "",
          type: EnumUserQuickLinksType.Organization,
          entityId: id,
        });
        existingOrgIds.add(id);
      }
      if (additions.length === 0) return links;
      return [...links, ...additions];
    },
    [enrolledOrganizationEntityIdSet, organizations],
  );

  const isRequiredOrganizationLink = useCallback(
    (ql: QuickLink) =>
      ql.type === EnumUserQuickLinksType.Organization &&
      enrolledOrganizationEntityIdSet.has(normalizeEntityId(ql.entityId)),
    [enrolledOrganizationEntityIdSet],
  );

  const persistQuickLinks = useCallback(
    async (links: QuickLink[]) => {
      if (!dbUser?._id) {
        throw new Error("Cannot save quick links: user is not loaded.");
      }
      const seen = new Set<string>();
      const deduped = mergeRequiredOrganizationQuickLinks(links).filter(ql => {
        const key = `${ql.type}:${normalizeEntityId(ql.entityId)}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      const userId = dbUser._id;
      const payload = deduped.map(ql => ({
        title: ql.title,
        link: ql.link,
        lastVisited: ql.lastVisited || new Date().toISOString(),
        type: ql.type as EnumUserQuickLinksType,
        entityId: normalizeEntityId(ql.entityId),
        pinned: ql.pinned ?? false,
      }));

      persistChainRef.current = persistChainRef.current.then(async () => {
        const isConcurrentQuickLinksError = (err: unknown) => {
          if (err instanceof ApolloError) {
            if (err.message.includes("concurrent updates")) return true;
            return err.graphQLErrors.some(e =>
              e.message.includes("concurrent updates"),
            );
          }
          const msg =
            err instanceof Error
              ? err.message
              : typeof err === "object" &&
                  err &&
                  "message" in err &&
                  typeof (err as { message: unknown }).message === "string"
                ? (err as { message: string }).message
                : String(err);
          return msg.includes("concurrent updates");
        };

        try {
          for (let attempt = 0; attempt < 3; attempt++) {
            if (attempt > 0) {
              await new Promise(r => setTimeout(r, 120 * attempt));
            }
            try {
              await saveQuickLinks({
                variables: {
                  id: userId,
                  baseQuickLinks: [],
                  quickLinks: payload,
                },
              });
              await refetchDbUser();
              return;
            } catch (mutationErr) {
              if (
                isConcurrentQuickLinksError(mutationErr) &&
                attempt < 2
              ) {
                await refetchDbUser();
                continue;
              }
              throw mutationErr;
            }
          }
        } catch (error) {
          trackPostHogException(error, {
            fileName: "useQuickLinks.ts",
            method: "persistQuickLinks",
            message: "Error saving quick links",
          });
          throw error;
        }
      });

      return persistChainRef.current;
    },
    [
      dbUser?._id,
      mergeRequiredOrganizationQuickLinks,
      refetchDbUser,
      saveQuickLinks,
      trackPostHogException,
    ],
  );

  const debouncedPersist = useCallback(
    (links: QuickLink[]) => {
      pendingLinksRef.current = links;
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        debounceTimerRef.current = undefined;
        const pending = pendingLinksRef.current;
        if (pending) {
          pendingLinksRef.current = null;
          persistQuickLinks(pending);
        }
      }, 500);
    },
    [persistQuickLinks],
  );

  // Flush debounced saves on unmount. Otherwise navigating away from Home (e.g. listing
  // card → operator) clears the timer and the UserSaveQuickLinks mutation never runs.
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = undefined;
      }
      const pending = pendingLinksRef.current;
      if (pending) {
        pendingLinksRef.current = null;
        void persistQuickLinks(pending);
      }
    };
  }, [persistQuickLinks]);

  const accessEntityIds = useMemo(
    () => ({
      organization: new Set(
        (dbUser?.access?.organizations ?? []).map(item =>
          normalizeEntityId(item?.entityId),
        ),
      ),
      operator: new Set(
        (dbUser?.access?.operators ?? [])
          .filter(
            item =>
              item?.status !== EnumUserAccessOperatorsStatus.Requested &&
              item?.status !== EnumUserAccessOperatorsStatus.NotInvited,
          )
          .map(item => normalizeEntityId(item?.entityId)),
      ),
      storefront: new Set(
        (dbUser?.access?.storefronts ?? []).map(item =>
          normalizeEntityId(item?.entityId),
        ),
      ),
    }),
    [
      dbUser?.access?.organizations,
      dbUser?.access?.operators,
      dbUser?.access?.storefronts,
    ],
  );

  const organizationById = useMemo(
    () =>
      new Map(
        organizations.map(org => [normalizeEntityId(org._id), org]),
      ),
    [organizations],
  );

  const isQuickLinkValid = useCallback(
    (quickLink: QuickLink): boolean => {
      const entityId = normalizeEntityId(quickLink.entityId);

      switch (quickLink.type) {
        case EnumUserQuickLinksType.Organization: {
          const inAccess = accessEntityIds.organization.has(entityId);
          if (
            !inAccess &&
            dbUser?.userType !== EnumUserUserType.TnbInternal
          ) {
            return false;
          }

          const organization = organizationById.get(entityId);
          const appSegment = getOrgAppSegment(quickLink.link);

          if (!organization) {
            if (!appSegment) return true;
            return (
              inAccess || dbUser?.userType === EnumUserUserType.TnbInternal
            );
          }

          if (!appSegment) return true;

          return !organization.metadata?.disabledApps?.some(
            disabledApp => disabledApp === appSegment,
          );
        }
        case EnumUserQuickLinksType.Operator:
          return accessEntityIds.operator.has(entityId);
        case EnumUserQuickLinksType.Storefront:
          return accessEntityIds.storefront.has(entityId);
        default:
          return true;
      }
    },
    [accessEntityIds, organizationById, dbUser?.userType],
  );

  const validPersistedQuickLinks = useMemo(() => {
    const filtered = persistedQuickLinks.filter(isQuickLinkValid);
    const mergedByKey = new Map<string, QuickLink>();
    const keyOrder: string[] = [];

    for (const ql of filtered) {
      const k = `${ql.type}:${normalizeEntityId(ql.entityId)}`;
      const existing = mergedByKey.get(k);
      if (!existing) {
        mergedByKey.set(k, {
          ...ql,
          entityId: normalizeEntityId(ql.entityId),
        });
        keyOrder.push(k);
      } else {
        mergedByKey.set(k, {
          ...existing,
          title: ql.title || existing.title,
          link: ql.link || existing.link,
          pinned: existing.pinned || ql.pinned,
          lastVisited:
            new Date(ql.lastVisited || 0).getTime() >
            new Date(existing.lastVisited || 0).getTime()
              ? ql.lastVisited
              : existing.lastVisited,
        });
      }
    }

    return keyOrder.map(k => mergedByKey.get(k)!);
  }, [persistedQuickLinks, isQuickLinkValid]);

  useEffect(() => {
    if (
      !dbUser?.email ||
      !dbUser?._id ||
      migrationAttempted.current ||
      persistedQuickLinks.length > 0
    ) {
      return;
    }

    migrationAttempted.current = true;
    const legacy = migrateLegacyQuickLinks(
      dbUser.email,
      trackPostHogException,
    );
    if (legacy.length > 0) {
      persistQuickLinks(legacy);
    }
  }, [
    dbUser?.email,
    dbUser?._id,
    persistedQuickLinks.length,
    persistQuickLinks,
    trackPostHogException,
  ]);

  useEffect(() => {
    if (!dbUser?._id) return;

    const needsPrune = persistedQuickLinks.length !== validPersistedQuickLinks.length;
    if (!needsPrune) {
      pruneAttempted.current = false;
      return;
    }

    if (pruneAttempted.current) return;
    pruneAttempted.current = true;
    debouncedPersist(validPersistedQuickLinks);
  }, [
    dbUser?._id,
    persistedQuickLinks,
    validPersistedQuickLinks,
    debouncedPersist,
  ]);

  const addQuickLink = useCallback(
    (
      title: string,
      link: string,
      type: EnumUserQuickLinksType,
      entityId: string,
    ) => {
      const working = pendingLinksRef.current ?? validPersistedQuickLinks;
      const existingIndex = working.findIndex(
        ql => ql.entityId === entityId && ql.type === type,
      );

      let updatedLinks: QuickLink[];
      if (existingIndex !== -1) {
        updatedLinks = working.map(ql =>
          ql.entityId === entityId && ql.type === type
            ? { ...ql, title, link, lastVisited: new Date().toISOString() }
            : ql,
        );
      } else {
        const newLink: QuickLink = {
          title,
          link,
          lastVisited: new Date().toISOString(),
          type,
          entityId,
        };
        updatedLinks = [...working, newLink];
      }

      const sortedUnpinned = _.orderBy(
        updatedLinks.filter(ql => !ql.pinned),
        ["lastVisited"],
        ["desc"],
      );
      debouncedPersist(buildWithPinnedSlots(updatedLinks, sortedUnpinned));
    },
    [validPersistedQuickLinks, debouncedPersist],
  );

  const removeQuickLink = useCallback(
    (entityId: string, type: EnumUserQuickLinksType) => {
      if (
        type === EnumUserQuickLinksType.Organization &&
        enrolledOrganizationEntityIdSet.has(normalizeEntityId(entityId))
      ) {
        return;
      }
      const working = pendingLinksRef.current ?? validPersistedQuickLinks;
      const filtered = working.filter(
        ql => !(ql.entityId === entityId && ql.type === type),
      );
      debouncedPersist(filtered);
    },
    [
      enrolledOrganizationEntityIdSet,
      validPersistedQuickLinks,
      debouncedPersist,
    ],
  );

  const updateQuickLinks = useCallback(
    (links: QuickLink[]) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = undefined;
      }
      pendingLinksRef.current = null;
      return persistQuickLinks(links);
    },
    [persistQuickLinks],
  );

  /**
   * Build the final quick links list:
   * - Pinned items hold their persisted array index (slot position)
   * - Remaining slots filled with: unpinned persisted (by lastVisited desc),
   *   then unvisited org defaults (always — enrolled orgs must appear),
   *   then unvisited operator defaults only until the user has saved once
   */
  const quickLinks = useMemo(() => {
    const unpinnedPersisted = validPersistedQuickLinks.filter(ql => !ql.pinned);

    const persistedEntityIds = new Set(
      validPersistedQuickLinks.map(
        ql => `${ql.type}:${normalizeEntityId(ql.entityId)}`,
      ),
    );

    const orgDefaults: QuickLink[] = (dbUser?.access?.organizations ?? [])
      .filter(
        a =>
          a?.entityId &&
          !persistedEntityIds.has(
            `ORGANIZATION:${normalizeEntityId(a.entityId)}`,
          ),
      )
      .map(a => {
        const org = organizations.find(o => o._id === a?.entityId);
        return {
          title: org
            ? getMultilingualString(org.name)
            : a?.entityId ?? "",
          link: `/app/organization/${a?.entityId}`,
          lastVisited: "",
          type: EnumUserQuickLinksType.Organization,
          entityId: a?.entityId ?? "",
        };
      })
      .filter(ql => ql.entityId);

    const operatorDefaults: QuickLink[] = (dbUser?.access?.operators ?? [])
      .filter(
        a =>
          a?.entityId &&
          a?.status !== EnumUserAccessOperatorsStatus.Requested &&
          a?.status !== EnumUserAccessOperatorsStatus.NotInvited &&
          !persistedEntityIds.has(`OPERATOR:${a.entityId}`),
      )
      .map(a => {
        const op = operators.find(o => o._entityId === a?.entityId);
        return {
          title: op
            ? getMultilingualString(op.name)
            : a?.entityId ?? "",
          link: `/app/operator/${a?.entityId}`,
          lastVisited: "",
          type: EnumUserQuickLinksType.Operator,
          entityId: a?.entityId ?? "",
        };
      })
      .filter(ql => ql.entityId);

    // Operator shortcuts only auto-fill until the first save (version 0). Organization
    // links for enrolled orgs always merge from access so they cannot disappear from the bar.
    const hasSavedQuickLinksOnce = (dbUser?.quickLinksVersion ?? 0) > 0;
    const includeOperatorDefaults =
      validPersistedQuickLinks.length === 0 && !hasSavedQuickLinksOnce;
    const unpinnedPool = [
      ...unpinnedPersisted,
      ...orgDefaults,
      ...(includeOperatorDefaults ? operatorDefaults : []),
    ];

    const built = buildWithPinnedSlots(validPersistedQuickLinks, unpinnedPool);
    const seenKeys = new Set<string>();
    return built.filter(ql => {
      const k = `${ql.type}:${normalizeEntityId(ql.entityId)}`;
      if (seenKeys.has(k)) return false;
      seenKeys.add(k);
      return true;
    });
  }, [
    validPersistedQuickLinks,
    dbUser?.quickLinksVersion,
    dbUser?.access?.organizations,
    dbUser?.access?.operators,
    organizations,
    operators,
  ]);

  return {
    quickLinks,
    addQuickLink,
    removeQuickLink,
    updateQuickLinks,
    isRequiredOrganizationLink,
  };
};
